
import React from "react";

export default function Process() {
  const stages = [
    {
      number: "01",
      title: "Site Inspection",
      description:
        "Comprehensive assessment of your property and detailed discussion of your vision",
      icon: "ri-search-eye-line",
    },
    {
      number: "02",
      title: "Design & Planning",
      description:
        "Detailed plans, material selection, and transparent cost breakdown",
      icon: "ri-draft-line",
    },
    {
      number: "03",
      title: "Construction",
      description:
        "Expert execution by licensed contractors with regular progress updates",
      icon: "ri-hammer-line",
    },
    {
      number: "04",
      title: "Quality Control",
      description: "Rigorous inspections at every stage to ensure perfection",
      icon: "ri-checkbox-circle-line",
    },
    {
      number: "05",
      title: "Handover",
      description: "Final walkthrough, documentation, and ongoing support",
      icon: "ri-key-line",
    },
  ];

  /** Safely render a stage element – protects against malformed data */
  const renderStage = (stage: typeof stages[number], idx: number) => {
    if (!stage?.number || !stage?.title) return null; // basic validation

    return (
      <div
        key={stage.number} // stable unique key
        className="flex flex-col items-center text-center"
      >
        {/* Icon */}
        <div className="w-8 h-8 flex items-center justify-center mb-4">
          <i className={`${stage.icon} text-2xl text-[#D4AF37]`}></i>
        </div>

        {/* Number badge */}
        <div
          className="relative w-20 h-20 bg-white border-4 border-[#D4AF37] rounded-full flex items-center justify-center shadow-lg mb-6"
          style={{
            boxShadow:
              idx === 0 || idx === stages.length - 1
                ? "0 0 20px rgba(212, 175, 55, 0.3)"
                : "none",
          }}
        >
          <span className="text-3xl font-serif text-[#D4AF37]">
            {stage.number}
          </span>
        </div>

        {/* Title & description */}
        <h3 className="text-xl font-bold text-[#2A2A2A] mb-3">{stage.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">
          {stage.description}
        </p>
      </div>
    );
  };

  return (
    <section
      id="process"
      className="py-16 md:py-24 lg:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2A2A2A] mb-4">
            Our Renovation Process
          </h2>
          <p className="text-base lg:text-lg text-gray-600">
            From first inspection to final handover—transparent, accountable,
            stress‑free
          </p>
        </div>

        {/* Timeline – Desktop (lg+) */}
        <div className="relative mt-16 md:mt-24 hidden lg:block">
          {/* Connecting line */}
          <div
            className="absolute top-10 left-0 right-0 h-0.5 border-t-2 border-dashed border-[#D4AF37]"
            style={{ marginLeft: "10%", marginRight: "10%" }}
          ></div>

          {/* Stages grid */}
          <div className="grid grid-cols-5 gap-8">
            {stages.map((stage, idx) => renderStage(stage, idx))}
          </div>
        </div>

        {/* Timeline – Mobile / Tablet */}
        <div className="mt-12 lg:hidden space-y-6">
          {stages.map((stage, idx) => {
            if (!stage?.number || !stage?.title) return null;

            return (
              <div
                key={stage.number}
                className="flex items-start gap-4 sm:gap-6"
              >
                {/* Number badge & vertical connector */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white border-4 border-[#D4AF37] rounded-full flex items-center justify-center shadow-md">
                    <span className="text-xl sm:text-2xl font-serif text-[#D4AF37]">
                      {stage.number}
                    </span>
                  </div>
                  {idx < stages.length - 1 && (
                    <div className="w-0.5 h-6 border-l-2 border-dashed border-[#D4AF37] mt-2"></div>
                  )}
                </div>

                {/* Content */}
                <div className="pt-2 sm:pt-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i
                        className={`${stage.icon} text-lg text-[#D4AF37]`}
                      ></i>
                    </div>
                    <h3 className="text-lg font-bold text-[#2A2A2A]">
                      {stage.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {stage.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
